import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ROUTES } from "../../../routes";
import { PortalMe, PortalMemberSummary } from "../models/PortalMe";
import { PortalSession, selectPortalMe, selectPortalMeStatus, selectPortalSession } from "../portal-slice";
import { fetchPortalMe } from "../slice-extensions/portal-slice-extension";
import { usePortalLocalization } from "./usePortalLocalization";
import { usePortalMember } from "./usePortalMember";

export interface PortalPage {
  session: PortalSession | null;
  me: PortalMe | null;
  /** The member the page shows (self or the ward in `?member=`); null until `me` loads. */
  memberId: string | null;
  /** Self first, then the wards; empty until `me` loads. */
  members: PortalMemberSummary[];
  selectMember: (memberId: string) => void;
}

/**
 * What every portal page repeats: back to the sign-in without a session, `me` fetched once per
 * sign-in, the member's language and the school's words applied, and the shown member resolved
 * from the URL.
 */
export const usePortalPage = (): PortalPage => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const session = useAppSelector(selectPortalSession);
  const me = useAppSelector(selectPortalMe);
  const meStatus = useAppSelector(selectPortalMeStatus);
  usePortalLocalization(me);
  const { memberId, selectMember } = usePortalMember(me);

  useEffect(() => {
    if (!session) navigate(ROUTES.PORTAL_LOGIN, { replace: true });
  }, [session, navigate]);

  useEffect(() => {
    if (session && meStatus === "idle") dispatch(fetchPortalMe());
  }, [session, meStatus, dispatch]);

  return { session, me, memberId, members: me ? [me.self, ...me.wards] : [], selectMember };
};
